
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { AlertTriangle, Merge as MergeIcon, XCircle, CheckCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { type MergeNodeData, type MergeValidation } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface MergeNodeProps {
    data: MergeNodeData;
    selected?: boolean;
}

/**
 * Merge Node - Represents structural convergence of alternative paths.
 * 
 * CRITICAL SEMANTICS:
 * - This is ONLY for structural convergence, NOT synchronization!
 * - In ASL, converging edges mean "one of these paths arrives" (MERGE)
 * - NOT "wait for all" (JOIN) - that's Parallel/Map's job
 * - Fan-in is allowed ONLY for mutually exclusive paths (e.g., from Choice)
 * 
 * INVALID USE:
 * - Cannot be used to represent Parallel/Map completion
 * - Cannot be used if multiple upstream paths can occur concurrently
 */
export const MergeNode = memo(function MergeNode({
    data,
    selected,
}: MergeNodeProps) {
    const { validation, validationMessage, incomingCount, label, layoutDirection = 'LR' } = data;
    
    // Determine if we're in horizontal (LR) or vertical (TB) flow
    const isHorizontal = layoutDirection === 'LR';
    
    // Bar size based on incoming paths
    const barSize = Math.max(60, incomingCount * 30);

    // Container dimensions based on orientation
    const containerStyle = isHorizontal 
        ? { width: 40, height: barSize }
        : { width: barSize, height: 40 };
    
    // Bar styling based on orientation
    const barClassName = isHorizontal
        ? 'absolute inset-x-0 mx-auto w-2 rounded-full'
        : 'absolute inset-y-0 my-auto h-2 rounded-full';
    
    const barStyle = isHorizontal
        ? { height: barSize, left: '50%', transform: 'translateX(-50%)' }
        : { width: barSize, top: '50%', transform: 'translateY(-50%)' };
    
    // Input handle position
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    
    // Output handle position
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;
    const outputStyle = isHorizontal 
        ? { top: '50%', transform: 'translateY(-50%)' }
        : { left: '50%', transform: 'translateX(-50%)' };

    const getValidationStyles = (validation: MergeValidation) => {
        switch (validation) {
            case 'valid':
                return {
                    bar: isHorizontal 
                        ? 'bg-gradient-to-b from-slate-300 to-slate-500' 
                        : 'bg-gradient-to-r from-slate-300 to-slate-500',
                    border: 'border-slate-400',
                    handle: '!border-slate-500 !bg-slate-400',
                    label: 'text-slate-600 bg-slate-100 border-slate-200',
                };
            case 'warning':
                return {
                    bar: isHorizontal 
                        ? 'bg-gradient-to-b from-amber-300 to-amber-500'
                        : 'bg-gradient-to-r from-amber-300 to-amber-500',
                    border: 'border-amber-400',
                    handle: '!border-amber-500 !bg-amber-400',
                    label: 'text-amber-700 bg-amber-50 border-amber-200',
                };
            case 'invalid':
                return {
                    bar: isHorizontal 
                        ? 'bg-gradient-to-b from-red-300 to-red-500'
                        : 'bg-gradient-to-r from-red-300 to-red-500',
                    border: 'border-red-400 border-2 border-dashed',
                    handle: '!border-red-500 !bg-red-400',
                    label: 'text-red-700 bg-red-50 border-red-200',
                };
        }
    };

    const styles = getValidationStyles(validation);

    // Label position based on orientation
    const labelStyle = isHorizontal
        ? 'absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap'
        : 'absolute top-1/2 -right-10 -translate-y-1/2 whitespace-nowrap';
    
    // XOR hint position based on orientation
    const xorStyle = isHorizontal
        ? 'absolute -left-6 top-1/2 -translate-y-1/2 cursor-help'
        : 'absolute -top-5 left-1/2 -translate-x-1/2 cursor-help';

    return (
        <TooltipProvider>
            <div className="relative" style={containerStyle}>
                {/* Validation indicator */}
                {validation !== 'valid' && (
                    <div className="absolute -right-2 -top-2 z-20">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className={cn(
                                    'flex h-5 w-5 items-center justify-center rounded-full',
                                    validation === 'invalid' 
                                        ? 'bg-red-100 border border-red-300' 
                                        : 'bg-amber-100 border border-amber-300'
                                )}>
                                    {validation === 'invalid' 
                                        ? <XCircle size={12} className="text-red-600" />
                                        : <AlertTriangle size={12} className="text-amber-600" />
                                    }
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                                {validation === 'invalid' ? (
                                    <>
                                        <p className="font-medium text-red-700">Invalid Merge</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {validationMessage || 'This merge would synchronize concurrent paths. Use Parallel join instead.'}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="font-medium text-amber-700">Review Required</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {validationMessage || 'Verify these paths are mutually exclusive.'}
                                        </p>
                                    </>
                                )}
                            </TooltipContent>
                        </Tooltip>
                    </div>
                )}

                {/* Input Handles - one for each incoming path */}
                {Array.from({ length: incomingCount }).map((_, idx) => {
                    const spacing = barSize / (incomingCount + 1);
                    const position = spacing * (idx + 1);
                    
                    const handleStyle = isHorizontal
                        ? { top: position, transform: 'translateY(-50%)' }
                        : { left: position, transform: 'translateX(-50%)' };

                    return (
                        <Handle
                            key={`incoming-${idx}`}
                            type="target"
                            position={inputPosition}
                            id={`incoming-${idx}`}
                            className={cn('!h-3 !w-3', styles.handle)}
                            style={handleStyle}
                        />
                    );
                })}

                {/* Merge bar - styled based on validation */}
                <div
                    className={cn(
                        barClassName,
                        styles.bar,
                        styles.border,
                        'shadow-sm',
                        selected && 'ring-2 ring-blue-500 ring-offset-2'
                    )}
                    style={barStyle}
                />

                {/* Merge indicator icon */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <MergeIcon 
                        size={12} 
                        className="text-white" 
                        style={{ transform: isHorizontal ? 'rotate(180deg)' : 'rotate(270deg)' }} 
                    />
                </div>

                {/* Output Handle */}
                <Handle
                    type="source"
                    id="source"
                    position={outputPosition}
                    className={cn('!h-3 !w-3', styles.handle)}
                    style={outputStyle}
                />

                {/* Label */}
                <div className={labelStyle}>
                    <span className={cn(
                        'text-[10px] font-medium px-1.5 py-0.5 rounded border',
                        styles.label
                    )}>
                        Merge
                    </span>
                </div>

                {/* Semantics hint */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className={xorStyle}>
                            <span className="text-[9px] text-slate-400">XOR</span>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side={isHorizontal ? 'left' : 'top'} className="max-w-xs">
                        <p className="font-medium">Structural Merge (XOR)</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            One of these paths arrives (not all). 
                            Valid only for mutually exclusive paths.
                        </p>
                    </TooltipContent>
                </Tooltip>
            </div>
        </TooltipProvider>
    );
});

